import { useEffect, useState } from 'react';

import Box, { type BoxProps } from '@liquidweb-dev/undercurrent/Box';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';

export interface FadeCollapseProps extends BoxProps {
	in: boolean;
	/** in milliseconds */
	collapseTimeout?: number;
	/** in milliseconds */
	fadeTimeout?: number;
	/**
	 * Removes the children from the page once collapsed, instead of leaving them
	 * hidden. Needed wherever hidden children would still be taking part in
	 * something - form inputs of the same name as visible ones, say.
	 */
	unmountOnExit?: boolean;
}

const FadeCollapse = ({
	children,
	in: inProp,
	collapseTimeout = 400,
	fadeTimeout = 100,
	unmountOnExit,
	sx,
	...rest
}: FadeCollapseProps) => {
	// children outlive `in` by the length of the collapse, so the content is
	// still there to animate away
	const [isMounted, setIsMounted] = useState(inProp);

	useEffect(() => {
		if (inProp) {
			setIsMounted(true);
			return undefined;
		}

		const timeout = setTimeout(() => setIsMounted(false), collapseTimeout);

		return () => clearTimeout(timeout);
	}, [inProp, collapseTimeout]);

	return (
		<Box
			sx={combineSx(
				{
					display: 'grid',
					gridTemplateRows: inProp ? '1fr' : '0fr',
					opacity: inProp ? 1 : 0,
					transition: `grid-template-rows ${collapseTimeout}ms ease-in-out, opacity ${fadeTimeout}ms ease-in-out`,
					overflow: 'hidden',
				},
				sx,
			)}
			{...rest}
		>
			<Box
				sx={{
					minHeight: '0',
					visibility: inProp ? 'visible' : 'hidden',
					transition: `visibility ${collapseTimeout}ms ease-in-out`,
				}}
			>
				{(!unmountOnExit || isMounted) && children}
			</Box>
		</Box>
	);
};

export default FadeCollapse;
